import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import Heading from "../../../../../../common/element/Heading";
import BtnLight from "../../../../../../common/element/Buttons/btnLight";
import BtnPrimary from "../../../../../../common/element/Buttons/btnPrimary";
import Input from "../../../../../../common/element/Input/Input";
import DateMultiSelectDropdown from "../../../../../../common/MultiSelectFilter/DateMultiSelectDropdown";
import AllProviderMultiSelectDropdown from "../../../../../../common/MultiSelectFilter/AllProviderMultiSelectDropdown";
import { Pagination } from "../../../../../../common/pagination";
import ReactPaginate from "react-paginate";
import { CSVLink } from "react-csv";
import ThreeDotBouncing from "../../../../../../common/element/PageLoader";
import { setBreadcrumbs } from "../../../../../../redux/classes/breadcrumbsSlice";
import useUnauthorizedHandler from "../../../../../../common/snippet/useUnauthorizedHandler";
import UnpaidRenewalsList from "./UnpaidRenewalsList";
import {
  GenerateUnpaidRenewalsReport,
} from "../../../../../../actions/Admin/reports/GenerateAdminUnpaidRenewalsReport";

const UnpaidRenewals = () => {
  const dispatch = useDispatch();
  const handleUnauthorizedAccess = useUnauthorizedHandler();
  const csvLinkRef = useRef(null);
  const [reportData, setReportData] = useState({ rows: [], total: 0 });
  const [isLoading, setIsLoading] = useState(false);

  const [filters, setFilters] = useState({
    search: "",
    provider_org_ids: [],
    due_date_range: "",
    last_contacted_range: "",
    page: 1,
    limit: 10,
    sort: "next_renewal_date",
    order: "asc",
  });

  const latestFiltersRef = useRef(filters);
  const hasFetchedInitialRef = useRef(false);

  const normalizedFilters = useMemo(() => {
    const normalizedProviders = Array.isArray(filters.provider_org_ids)
      ? filters.provider_org_ids
          .map((item) =>
            item?.provider_org_id ??
            item?.organisation_id ??
            item?.value ??
            item
          )
          .filter((entry) => entry !== undefined && entry !== null)
      : [];

    const base = {
      ...filters,
      provider_org_ids: normalizedProviders,
    };

    const cleanedEntries = Object.entries(base).reduce(
      (accumulator, [key, value]) => {
        if (value == null) {
          return accumulator;
        }

        if (typeof value === "string") {
          const trimmed = value.trim();
          if (!trimmed) {
            return accumulator;
          }
          accumulator[key] = trimmed;
          return accumulator;
        }

        if (Array.isArray(value)) {
          if (!value.length) {
            return accumulator;
          }
          accumulator[key] = value;
          return accumulator;
        }

        accumulator[key] = value;
        return accumulator;
      },
      {}
    );

    return cleanedEntries;
  }, [filters]);

  const extractRowsAndTotal = useCallback((payload) => {
    if (!payload) {
      return { rows: [], total: 0 };
    }

    if (Array.isArray(payload)) {
      return { rows: payload, total: payload.length };
    }

    const directRows = Array.isArray(payload?.rows) ? payload.rows : null;
    const nestedDataRows = Array.isArray(payload?.data?.rows)
      ? payload.data.rows
      : null;
    const dataArray = Array.isArray(payload?.data) ? payload.data : null;

    const rows = directRows ?? nestedDataRows ?? dataArray ?? [];

    const totalCandidate =
      payload?.total ??
      payload?.total_count ??
      payload?.count ??
      payload?.data?.total ??
      payload?.data?.total_count ??
      payload?.data?.count ??
      rows.length;

    const total = Number(totalCandidate) || rows.length || 0;

    return { rows: Array.isArray(rows) ? rows : [], total };
  }, []);

  const fetchUnpaidRenewals = useCallback(
    async (activeFilters) => {
      if (!activeFilters) {
        return null;
      }

      setIsLoading(true);

      try {
        const response = await dispatch(
          GenerateUnpaidRenewalsReport(activeFilters)
        );
        const successStatuses = new Set([
          "GETSUCCESS",
          "SUCCESS",
          "true",
          "OK",
        ]);

        if (response?.success === true || successStatuses.has(response?.status)) {
          const parsedPayload = extractRowsAndTotal(response?.data ?? response);
          setReportData(parsedPayload);
        } else if (response?.status === 401) {
          handleUnauthorizedAccess();
        } else {
          console.warn("Unpaid Renewals response without data", response);
          setReportData({ rows: [], total: 0 });
        }

        return response;
      } catch (error) {
        console.error("Error fetching Unpaid Renewals report:", error);
        setReportData({ rows: [], total: 0 });
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [dispatch, handleUnauthorizedAccess, extractRowsAndTotal]
  );

  useEffect(() => {
    dispatch(
      setBreadcrumbs([
        {
          label: "Reports",
          url: "/admin/reports",
          home: "/admin/dashboard",
        },
        {
          label: "Unpaid renewals",
          url: "/admin/reports/unpaid-renewals",
        },
      ])
    );
  }, [dispatch]);

  useEffect(() => {
    if (!hasFetchedInitialRef.current) {
      hasFetchedInitialRef.current = true;
      latestFiltersRef.current = normalizedFilters;
      fetchUnpaidRenewals(normalizedFilters);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const nextString = JSON.stringify(normalizedFilters);
    if (JSON.stringify(latestFiltersRef.current) === nextString) {
      return;
    }

    latestFiltersRef.current = JSON.parse(nextString);
    fetchUnpaidRenewals(latestFiltersRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [normalizedFilters]);

  const updateFilters = useCallback((field, value) => {
    setFilters((prev) => {
      if (field === "page" || field === "limit") {
        return {
          ...prev,
          [field]: value,
        };
      }

      return {
        ...prev,
        [field]: value,
        page: 1,
      };
    });
  }, []);

  const handlePageChange = ({ selected }) => {
    const nextPage = selected + 1;
    updateFilters("page", nextPage);
  };

  const handlePageLimitChange = useCallback(
    (nextState) => {
      const normalized = nextState ?? {};
      const parsedLimit = Number(normalized?.limit);
      if (!Number.isFinite(parsedLimit)) {
        return;
      }
      updateFilters("limit", parsedLimit);
    },
    [updateFilters]
  );

  const totalPages = useMemo(() => {
    const totalCount = Number(reportData?.total) || 0;
    const pageSize = Number(filters.limit) || 10;
    return totalCount > 0 ? Math.ceil(totalCount / pageSize) : 1;
  }, [reportData?.total, filters.limit]);

  const processedCsvData = useMemo(() => {
    const rows = Array.isArray(reportData?.rows) ? reportData.rows : [];
    return rows.map((row) => ({
      "Provider Name": row?.provider_trading_name ?? "-",
      "Provider Type": row?.provider_type_name ?? "-",
      "Renewal Due Date": row?.upcoming_invoice_due_date ?? row?.next_renewal_date ?? "-",
      "Licence Seats": row?.licence_seats ?? "-",
      "Worker Seats": row?.worker_seats ?? "-",
      "Assignment Seats": row?.assignment_seats ?? "-",
      "Invoice Amount": row?.upcoming_invoice_amount ?? "-",
    }));
  }, [reportData?.rows]);

  const handleExport = () => {
    if (!processedCsvData.length || !csvLinkRef.current) {
      return;
    }

    csvLinkRef.current.link.click();
  };

  const refreshReport = () => {
    fetchUnpaidRenewals(latestFiltersRef.current);
  };

  const hasResults = Array.isArray(reportData?.rows) && reportData.rows.length > 0;

  return (
    <div className="px-5 py-6 lg:px-10 lg:py-8">
      <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
        <Heading
          title="Unpaid renewals"
          subtitle="Contractors with outstanding renewals"
        />
        <div className="flex gap-3">
          <BtnLight
            className="min-w-[120px]"
            onClick={refreshReport}
            disabled={isLoading}
          >
            Refresh
          </BtnLight>
          <BtnPrimary
            className="min-w-[160px]"
            onClick={handleExport}
            disabled={isLoading || !processedCsvData.length}
          >
            Export CSV
          </BtnPrimary>
          <CSVLink
            data={processedCsvData}
            filename="unpaid-renewals.csv"
            ref={csvLinkRef}
            style={{ display: "none" }}
          />
        </div>
      </div>

      <div className="mt-6 grid gap-4 rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <div className="flex flex-col gap-2">
            <span className="text-sm font-medium text-gray-700">Search</span>
            <Input
              className="h-10"
              placeholder="Search by provider"
              value={filters.search}
              inputchangehandler={(value) => updateFilters("search", value)}
            />
          </div>

          <div className="flex flex-col gap-2">
            <span className="text-sm font-medium text-gray-700">Provider</span>
            <AllProviderMultiSelectDropdown
              title="Provider"
              updateFilter={(field, value) => updateFilters(field, value)}
              Path="provider"
            />
          </div>

          <div className="flex flex-col gap-2">
            <span className="text-sm font-medium text-gray-700">Renewal due</span>
            <DateMultiSelectDropdown
              title="Renewal due"
              type="due_date_range"
              updateFilter={(field, value) => updateFilters(field, value)}
            />
          </div>

          <div className="flex flex-col gap-2">
            <span className="text-sm font-medium text-gray-700">Last contacted</span>
            <DateMultiSelectDropdown
              title="Last contacted"
              type="last_contacted_range"
              updateFilter={(field, value) => updateFilters(field, value)}
            />
          </div>
        </div>
      </div>

      <div className="mt-6 rounded-lg border border-gray-200 bg-white">
        {isLoading && reportData.rows.length === 0 ? (
          <div className="flex justify-center py-16">
            <ThreeDotBouncing />
          </div>
        ) : (
          <div className="p-5">
            <UnpaidRenewalsList rows={reportData?.rows} />
          </div>
        )}

        <div className="flex flex-col items-center gap-4 border-t border-gray-100 px-4 py-5 md:flex-row md:justify-between">
          <Pagination
            totalCount={reportData?.total || 0}
            pageNumber={filters.page}
            displayPage={reportData?.rows?.length || 0}
            UpdatePageLimit={(next) =>
              handlePageLimitChange({ limit: next?.limit ?? next })
            }
            UpdatePage={(nextPage) => updateFilters("page", nextPage)}
            allfilters={{ limit: filters.limit }}
          />

          <ReactPaginate
            pageCount={totalPages}
            forcePage={Math.max(Number(filters.page) - 1, 0)}
            onPageChange={handlePageChange}
            marginPagesDisplayed={1}
            pageRangeDisplayed={2}
            containerClassName="flex items-center gap-2"
            activeClassName="bg-primary-600 text-white"
            pageLinkClassName="rounded-md px-3 py-1 text-sm text-gray-700 hover:bg-gray-100"
            previousLabel={
              <span className="rounded-md px-3 py-1 text-sm text-gray-700 hover:bg-gray-100">
                Prev
              </span>
            }
            nextLabel={
              <span className="rounded-md px-3 py-1 text-sm text-gray-700 hover:bg-gray-100">
                Next
              </span>
            }
            breakLabel={<span className="text-sm text-gray-500">…</span>}
          />
        </div>
      </div>
    </div>
  );
};

export default UnpaidRenewals;
